import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { Modal } from '@/shared/components/Modal'
import { EmptyState } from '@/shared/components/EmptyState'
import { StarIcon } from '@/shared/components/Icons'
import { TEMPLATES, filterTemplates, splitPopular, type Template } from '@/features/policies/lib/regoTemplates'
import type { PolicyArtefactType } from '@/lib/apiTypes'
import s from '@/features/policies/components/TemplateGallery.module.css'

/** Capitalised for the dialog title, matching its sibling ("Provider policy input"). */
const ARTEFACT_LABEL: Record<PolicyArtefactType, string> = {
  module: 'Module', state: 'State', provider: 'Provider', plan: 'Plan',
}

const ARTEFACT_ORDER: PolicyArtefactType[] = ['module', 'state', 'provider', 'plan']

interface TemplateGalleryProps {
  /** The artefact the POLICY is scoped to, and the set the gallery opens on. Only this set can be
   *  inserted — unless `anyArtefact` says the caller has not committed to an artefact yet. */
  artefactType: PolicyArtefactType
  /**
   * The caller has no policy open yet, so every set is insertable and `onPick` is told which one
   * the card came from. This is the Policies overview, which opens the gallery FIRST and the
   * policy form second — the read-only asymmetry below exists because the editor's buffer already
   * belongs to one artefact, which is not true before a policy exists.
   */
  anyArtefact?: boolean
  /** Picked a card. Gets the WHOLE template, so the caller can fill a name and description too,
   *  plus the artefact set it came from (the same one as `artefactType` unless `anyArtefact`). */
  onPick: (t: Template, artefactType: PolicyArtefactType) => void
  onClose: () => void
}

/**
 * The template picker: a searchable grid of every starting point for this artefact, curated ones
 * first with a star.
 *
 * The other artefacts' sets can be BROWSED from here but not inserted, and that asymmetry is the
 * point rather than an omission. A policy is authored against exactly one artefact and evaluated
 * only for that one, so a module template dropped into a provider policy reads input.module.* on a
 * document that has no such field: it parses, it saves, and its rule bodies never match. Nothing
 * downstream would report that. So the other sets are readable - for deciding what to go and write
 * next - and their cards are not buttons.
 *
 * `anyArtefact` lifts that restriction for the one caller that has no policy yet: from the Policies
 * overview the artefact is still undecided, so picking a card is what decides it.
 *
 * This usually opens from inside the policy editor, which is itself a Modal inside a <form>. Two
 * consequences, neither obvious from the JSX: every button carries an explicit type="button", and
 * Enter in the search box is swallowed, because otherwise it is an implicit submit of that form.
 */
export function TemplateGallery({ artefactType, anyArtefact = false, onPick, onClose }: TemplateGalleryProps) {
  const [viewing, setViewing] = useState<PolicyArtefactType>(artefactType)
  const [query, setQuery] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)
  const uid = useId()

  const browsing = !anyArtefact && viewing !== artefactType
  const all = TEMPLATES[viewing] ?? TEMPLATES.module
  const { popular, rest } = useMemo(() => splitPopular(filterTemplates(all, query)), [all, query])
  const shown = popular.length + rest.length

  // Focus the search box, but after the parent Modal's effect: a child's effect runs first, and
  // the showModal() that follows would take the focus straight back to the dialog's own ✕.
  useEffect(() => {
    const frame = requestAnimationFrame(() => searchRef.current?.focus())
    return () => cancelAnimationFrame(frame)
  }, [])

  function switchTo(next: PolicyArtefactType) {
    setViewing(next)
    // Carrying the filter across the switch can land on an empty grid, which reads as "this
    // artefact has no templates" rather than "your search matched none of them".
    setQuery('')
  }

  function choose(t: Template) {
    onPick(t, viewing)
    onClose()
  }

  return (
    <Modal
      track="policy_template_gallery"
      // The artefact belongs in the TITLE, not in grey subtext: a template only reads fields that
      // exist on its own artefact, so which set this is is the first thing to know about it.
      title={`${ARTEFACT_LABEL[viewing]} policy templates`}
      onClose={onClose}
      maxWidth={960}
      fillHeight
    >
      <div className={s.wrap}>
        {browsing ? (
          <div className={s.noticeRow}>
            <button type="button" className={s.backBtn} onClick={() => switchTo(artefactType)}>
              ← Back to {ARTEFACT_LABEL[artefactType].toLowerCase()} templates
            </button>
            <span className={s.notice}>
              Browsing only. These read <code>input.{viewing}.*</code>, so they only do anything on
              a {ARTEFACT_LABEL[viewing].toLowerCase()} policy — start one from the{' '}
              {ARTEFACT_LABEL[viewing]} tab.
            </span>
          </div>
        ) : anyArtefact ? (
          // Scopeless: the set is the choice, not a detour, so all four are listed with the
          // current one marked rather than filtered out.
          <div className={s.switchRow}>
            <span className={s.switchLabel}>Artefact:</span>
            {ARTEFACT_ORDER.map((a) => (
              <button
                key={a}
                type="button"
                className={`${s.switchBtn} ${a === viewing ? s.switchBtnActive : ''}`}
                aria-pressed={a === viewing}
                onClick={() => switchTo(a)}
              >
                {ARTEFACT_LABEL[a]}
              </button>
            ))}
          </div>
        ) : (
          <div className={s.switchRow}>
            <span className={s.switchLabel}>Also browse:</span>
            {ARTEFACT_ORDER.filter((a) => a !== artefactType).map((a) => (
              <button key={a} type="button" className={s.switchBtn} onClick={() => switchTo(a)}>
                {ARTEFACT_LABEL[a]}
              </button>
            ))}
          </div>
        )}

        <div className={s.searchRow} role="search">
          <input
            ref={searchRef}
            // Deliberately not type="search": WebKit's clear affordance eats the first Escape,
            // which would make Escape-to-close behave differently in Safari.
            type="text"
            className={`input ${s.search}`}
            placeholder="Search templates…"
            aria-label={`Search ${ARTEFACT_LABEL[viewing].toLowerCase()} policy templates by name or description`}
            aria-describedby={`${uid}-count`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault() }}
          />
          <div id={`${uid}-count`} className={s.count} role="status" aria-live="polite">
            {query.trim() ? `${shown} of ${all.length}` : `${all.length} templates`}
          </div>
        </div>

        <div className={s.scroll}>
          {shown === 0 ? (
            <EmptyState
              icon="⌕"
              title="No template matches that search"
              subtitle="Templates match on their name and description. Try a shorter search, or close this and write the policy from scratch."
            />
          ) : (
            <>
              {popular.length > 0 && (
                <Group id={`${uid}-popular`} heading="Popular" items={popular} insertable={!browsing} onChoose={choose} />
              )}
              {rest.length > 0 && (
                <Group
                  id={`${uid}-rest`}
                  heading={popular.length > 0 ? 'All templates' : 'Templates'}
                  items={rest}
                  insertable={!browsing}
                  onChoose={choose}
                />
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  )
}

function Group({ id, heading, items, insertable, onChoose }: {
  id: string
  heading: string
  items: Template[]
  /** False while browsing another artefact's set: the cards are read-only, so they are not buttons. */
  insertable: boolean
  onChoose: (t: Template) => void
}) {
  return (
    <section className={s.group} aria-labelledby={id}>
      <h3 id={id} className={s.groupHead}>{heading}</h3>
      <div className={s.grid}>
        {items.map((t) => {
          const body = (
            <>
              <span className={s.cardHead}>
                <span className={s.cardLabel}>{t.label}</span>
                {/* The star is decorative: the group heading above already says "Popular", and
                    repeating it on every card is noise in a screen reader. */}
                {t.popular && (
                  <span className={s.star} title="Popular choice">
                    <StarIcon size={11} filled />
                  </span>
                )}
              </span>
              <span className={s.cardDesc}>{t.description}</span>
            </>
          )
          return insertable ? (
            <button key={t.label} type="button" className={s.card} onClick={() => onChoose(t)}>
              {body}
            </button>
          ) : (
            <div key={t.label} className={`${s.card} ${s.cardReadOnly}`}>{body}</div>
          )
        })}
      </div>
    </section>
  )
}
